import { useState, useEffect, useRef, useCallback } from "react";
import { useRecoilValue } from "recoil";
import { authState } from "../recoil/authAtom";
import { io } from "socket.io-client";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../constants";

const SNOOZE_KEY = "litss_password_expiry_snooze";

export const usePasswordExpiryNotification = () => {
  const auth = useRecoilValue(authState);
  const [expiryData, setExpiryData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const socketRef = useRef(null);

  const cleanBaseUrl = (BASE_URL || "http://localhost:8000/").replace(
    /\/$/,
    "",
  );

  // Resolve current user identifiers strictly when authenticated
  const currentUserId = auth ? auth.userId || auth._doc?.userId || null : null;

  const currentUserRefId = auth
    ? auth._id || auth._doc?._id || auth.id || null
    : null;

  const currentUserName = auth
    ? auth.userName || auth._doc?.userName || "User"
    : "User";

  // Reset expiry state when user is unauthenticated
  useEffect(() => {
    if (!auth) {
      setExpiryData(null);
      setIsModalOpen(false);
    }
  }, [auth]);

  // Check if modal was snoozed in this browser session
  const isSnoozed = useCallback(() => {
    try {
      const snoozedUntil = Number(sessionStorage.getItem(SNOOZE_KEY) || 0);
      return Date.now() < snoozedUntil;
    } catch {
      return false;
    }
  }, []);

  // Handle incoming notification payload
  const processNotification = useCallback(
    (payload, forceShow = false) => {
      if (!payload) return;

      const targetUserId = payload.userId;
      const targetUserRefId = payload.userRefId
        ? String(payload.userRefId)
        : null;
      const myUserId = currentUserId ? String(currentUserId).trim() : null;
      const myRefId = currentUserRefId ? String(currentUserRefId).trim() : null;

      // Ensure notification is targeted for this user
      const isForMe =
        (!targetUserId && !targetUserRefId) ||
        (myUserId &&
          targetUserId &&
          myUserId === String(targetUserId).trim()) ||
        (myRefId && targetUserRefId && myRefId === targetUserRefId);

      if (!isForMe) return;

      const formatted = {
        userId: payload.userId || myUserId,
        userRefId: payload.userRefId || myRefId,
        userName: payload.userName || currentUserName,
        lastPasswordChanged: payload.lastPasswordChanged,
        diffDays: Number(payload.diffDays ?? 0),
        isExpired: Boolean(payload.isExpired ?? payload.requiresPasswordChange),
        daysRemaining: Number(payload.daysRemaining ?? 0),
        requiresPasswordChange: Boolean(
          payload.requiresPasswordChange ?? payload.isExpired,
        ),
        title: payload.title || "Password Change Notification",
        message:
          payload.message ||
          (payload.isExpired
            ? `Your password was last changed ${payload.diffDays || 180} days ago. Please change your password for security purposes.`
            : `Your password is valid. ${payload.daysRemaining ?? 0} day(s) remaining until change is required.`),
        timestamp: payload.timestamp || new Date(),
      };

      setExpiryData(formatted);

      if (!formatted.isExpired) {
        setIsModalOpen(false);
        return;
      }

      if (formatted.isExpired) {
        // Display toast notification
        toast.warn(formatted.message, {
          toastId: "password-expiry-toast",
          autoClose: 8000,
          position: "top-right",
        });

        // Open modal if forced or not actively snoozed
        if (forceShow || !isSnoozed()) {
          setIsModalOpen(true);
        }
      }
    },
    [currentUserId, currentUserRefId, currentUserName, isSnoozed],
  );

  // REST API query to check password expiry status
  const checkPasswordExpiry = useCallback(
    async (forceShow = false) => {
      if (!currentUserId && !currentUserRefId) return;

      setIsChecking(true);
      try {
        const queryParams = new URLSearchParams();
        if (currentUserId) queryParams.append("userId", currentUserId);
        if (currentUserRefId) queryParams.append("userRefId", currentUserRefId);

        const url = `${cleanBaseUrl}/api/user/common/check-password-expiry?${queryParams.toString()}`;
        const response = await axios.get(url, {
          withCredentials: true,
        });

        const data = response.data;
        if (data?.success) {
          const payload = data.data || data;
          processNotification(payload, forceShow);
        }
      } catch (err) {
        // Fallback: try POST method if GET rejected
        try {
          const response = await axios.post(
            `${cleanBaseUrl}/api/user/common/check-password-expiry`,
            {
              userId: currentUserId,
              userRefId: currentUserRefId,
            },
            { withCredentials: true },
          );
          if (response.data?.success) {
            const payload = response.data.data || response.data;
            processNotification(payload, forceShow);
          }
        } catch (postErr) {
          console.warn("Check password expiry failed:", err.message);
        }
      } finally {
        setIsChecking(false);
      }
    },
    [cleanBaseUrl, currentUserId, currentUserRefId, processNotification],
  );

  // Setup Socket.IO listener for real-time notifications
  useEffect(() => {
    if (!currentUserId && !currentUserRefId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const socket = io(cleanBaseUrl, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      autoConnect: true,
      query: {
        userId: currentUserId || "",
        userRefId: currentUserRefId || "",
      },
      auth: {
        userId: currentUserId || "",
        userRefId: currentUserRefId || "",
      },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      // Join user specific rooms matching backend controller emissions
      if (currentUserId) {
        socket.emit("join", `user_${currentUserId}`);
        socket.emit("joinRoom", `user_${currentUserId}`);
        socket.emit("join-user", `user_${currentUserId}`);
      }
      if (currentUserRefId) {
        socket.emit("join", `user_${currentUserRefId}`);
        socket.emit("joinRoom", `user_${currentUserRefId}`);
        socket.emit("join-user", `user_${currentUserRefId}`);
      }
      socket.emit("register", {
        userId: currentUserId,
        userRefId: currentUserRefId,
      });

      // Once connected, also verify current expiry status
      checkPasswordExpiry();
    });

    // Listen for backend socket events
    const handleEvent = (payload) => {
      processNotification(payload);
    };

    const handlePasswordChangedSocket = () => {
      setExpiryData(null);
      setIsModalOpen(false);
      try {
        sessionStorage.removeItem(SNOOZE_KEY);
      } catch {
        // ignore
      }
      checkPasswordExpiry();
    };

    socket.on("passwordChangeNotification", handleEvent);
    socket.on("password-change-notification", handleEvent);
    socket.on("passwordChanged", handlePasswordChangedSocket);
    socket.on("password-changed", handlePasswordChangedSocket);
    socket.on("passwordUpdated", handlePasswordChangedSocket);
    socket.on("password-updated", handlePasswordChangedSocket);

    return () => {
      socket.off("passwordChangeNotification", handleEvent);
      socket.off("password-change-notification", handleEvent);
      socket.off("passwordChanged", handlePasswordChangedSocket);
      socket.off("password-changed", handlePasswordChangedSocket);
      socket.off("passwordUpdated", handlePasswordChangedSocket);
      socket.off("password-updated", handlePasswordChangedSocket);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    cleanBaseUrl,
    currentUserId,
    currentUserRefId,
    processNotification,
    checkPasswordExpiry,
  ]);

  // Listen for in-app password change completion (e.g. from VerifyOtp)
  useEffect(() => {
    const handleInAppPasswordChanged = () => {
      setExpiryData(null);
      setIsModalOpen(false);
      try {
        sessionStorage.removeItem(SNOOZE_KEY);
      } catch {
        // ignore
      }
      if (currentUserId || currentUserRefId) {
        checkPasswordExpiry();
      }
    };

    window.addEventListener(
      "litss:password-changed",
      handleInAppPasswordChanged,
    );
    return () => {
      window.removeEventListener(
        "litss:password-changed",
        handleInAppPasswordChanged,
      );
    };
  }, [checkPasswordExpiry, currentUserId, currentUserRefId]);

  // Initial check on mount/login
  useEffect(() => {
    if (currentUserId || currentUserRefId) {
      checkPasswordExpiry();
    }
  }, [currentUserId, currentUserRefId, checkPasswordExpiry]);

  // Actions
  const openModal = () => setIsModalOpen(true);

  const closeModal = () => setIsModalOpen(false);

  const clearExpiry = () => {
    setExpiryData(null);
    setIsModalOpen(false);
    try {
      sessionStorage.removeItem(SNOOZE_KEY);
    } catch {
      // ignore
    }
  };

  const snooze = (minutes = 30) => {
    try {
      const snoozeUntil = Date.now() + minutes * 60 * 1000;
      sessionStorage.setItem(SNOOZE_KEY, String(snoozeUntil));
    } catch {
      // ignore
    }
    setIsModalOpen(false);
  };

  return {
    expiryData,
    isModalOpen,
    isChecking,
    openModal,
    closeModal,
    clearExpiry,
    snooze,
    checkPasswordExpiry,
    currentUserId,
    currentUserRefId,
    currentUserName,
  };
};
